import React from 'react';
import { isSameDay } from 'date-fns';
import { Trans } from 'react-i18next';
import { useMutation } from 'react-query';

import { ToastVariant, useDateFns, useProvider, useToastMessage } from '~/Hooks';
import { IEvent } from '~/Models/DTO/Event.model';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { createQueriedURL } from '~/Helpers/String.helper';
import { CORE_SERVICE_URLS, EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { extractErrorMessage } from '~/Helpers/Error.helper';
import { IBookingSummaryEvent } from '~/Models/DTO/Booking.models';
import { ICalendarEventBookings } from '~/Screens/Calendar/interfaces';

export const useCalendarEventBookings = () => {
  const { showToast } = useToastMessage();
  const { currentProvider } = useProvider();
  const { convertToProviderTimezone } = useDateFns();

  const eventBookings = useMutation(
    REQUEST_ACTIONS.GET_NEXT_EVENTS,
    async ({
      event,
      newEvent,
    }: {
      event: IEvent;
      newEvent: IEvent;
    }): Promise<ICalendarEventBookings> => {
      const nextEvents = await requestReactQuery<IEvent[]>({
        url: createQueriedURL(EVENT_SERVICE_URLS.EVENT_LIST, {
          providerId: currentProvider?.uuid,
          eventGroupId: event.eventGroupId,
          size: 2,
          from: convertToProviderTimezone(new Date().toString()).toISOString(),
        }),
        config: { method: 'GET' },
      });
      const bookingSummary = await requestReactQuery<IBookingSummaryEvent[]>({
        url: CORE_SERVICE_URLS.BOOKING_SUMMARY,
        data: { eventIds: [event.id] },
        config: { method: 'POST' },
      });
      const bookingsCounter = await requestReactQuery<{ label: string; count: number }[]>({
        url: CORE_SERVICE_URLS.COUNT_BOOKINGS,
        config: { method: 'GET' },
        apiVersion: '2',
        params: {
          domain: currentProvider?.uuid,
          granularity: 'Total',
          begin: new Date().toISOString(),
          eventSeriesId: event.eventSerieId,
        },
      });
      return {
        hasMoreThenOneEvent:
          isSameDay(new Date(event.start), new Date(newEvent.start)) && nextEvents?.length > 1,
        eventHasBookings: !!bookingSummary?.[0].bookingsBooked,
        eventSeriesHasBookings: !!bookingsCounter?.[0].count,
      };
    },
    {
      onError: (errorObj: IErrorResponse) => {
        const message = extractErrorMessage(errorObj) as any;
        showToast(
          'Event update error',
          <Trans i18nKey={message?.key} defaults={message?.defaultMessage || message} />,
          ToastVariant.ERROR,
        );
      },
    },
  );

  return { eventBookings };
};
